import { Component, Inject, Injector, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { AppLoadingIndicatorService } from './services/app-loading-indicator.service';
import * as FullStory from '@fullstory/browser';
import { AppLocalizationService } from './services/AppLocalizationService';
import { AppStorageService } from './services/app-storage.service';
import { DestroyBase } from './components/common/ng-destroy-base';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { DOCUMENT } from '@angular/common';

/**
 * App main component
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent extends DestroyBase implements OnInit {
  /**
   * Constructor
   * @param http HttpClient
   * @param translate TranslateService
   * @param appLocalization AppLocalizationService
   * @param appStorage AppStorageService
   * @param loadingIndicator AppLoadingIndicatorService
   * @param injector Injector
   * @param document Document
   */
  constructor(private http: HttpClient,
    private translate: TranslateService,
    private appLocalization: AppLocalizationService,
    private appStorage: AppStorageService,
    private loadingIndicator: AppLoadingIndicatorService,
    private injector: Injector,
    @Inject(DOCUMENT) document: Document
  ) {
    super();
    //acquireTokenSilent cause page refresh #526
    //https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/526
    //https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/9fa581aa01e5314dc765ea33161b431b6f3e6c04/lib/msal-angular/samples/MSALAngularDemoApp/src/app/app.component.ts
    //https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/1a7b3c5fdb67d160a216eabe09c4c004e3310649/lib/msal-angular/samples/MSALAngularDemoApp/src/app/app.component.html

    this.window = document.defaultView;
  }

  /**
   * Global window object
   */
  private _window: any = window;

  /**
   * Global window object
   */
  private window: Window;

  /**
   * Is the application running in an iframe?
   */
  public get isIframe(): boolean {
    const window = this.window;
    return window !== window.parent && !window.opener;
  }

  /**
   * Set to true if the app is initialized (user context is loaded)
   */
  public appInitialized = false;

  /**
   * Controls the loading indicator
   */
  public isLoading = false;

  /**
   * Pendo object on global window object
   */
  private get pendo() {
    return this.window['pendo'];
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    if (!this.isIframe) {

      if (environment.UseMsal && !this._window.apiKeyInfo) {
        const authService = this.injector.get(MsalService);
        authService.handleRedirectObservable().subscribe({
          next: (_result: AuthenticationResult) => {
            // Perform actions related to user accounts here
            this.initializeComponent();
          },
          error: (error) => console.log(error)
        });
      } else {
        this.initializeComponent();
      }
    }
  }

  /**
   * Initializes the component
   */
  initializeComponent() {
    if (!this.isIframe) {
      this.setupMsalTokenPing();

      this.translate.setDefaultLang('en-US');

      this.subscriptions.add(this.loadingIndicator.show$.subscribe((isLoading) => {
        setTimeout(() => {
          this.isLoading = isLoading;
        });
      }));

      console.log('Loading user data');

      const window = this.window;
      Promise.all([
        this.appLocalization.init(),
        this.appStorage.loadCurrentUserDetails(),
        this.appStorage.reloadRoleAssignments()
      ]).then(([_appLocalizationResult, userInfo, _rolesResult]) => {
        this.handleDataLoaded(userInfo, window);
      }).catch(error => {
        this.handleError(error, window);
      });
    }
  }

  /**
   * For Non-JLL deployments using MSAL: Ping an endpoint to check the current MSAL token
   */
  private setupMsalTokenPing() {
    if (environment.UseMsal) {
      const tokenPingUrl = environment.POLLUX_API_URL + '/status/ping';

      const pingInterval = 3 * 60 * 1000; //every 3 minutes
      this.pingTokenUrl(tokenPingUrl, pingInterval);
    }
  }

  /**
   * Called once the main data is loaded. Initializes the application if we have a valid user account
   * @param userInfo
   * @param window
   */
  private handleDataLoaded(userInfo: any, window: Window) {
    console.log('User data loaded!');
    if (userInfo) {
      const userEmail = userInfo.User_Email.toLowerCase(); //Task 18622: Upper/lower case IDs in Pendo: use lowercase
      const userDescription = userInfo.User_Description;
      if (window.location.hash === "#/") {
        window.location.hash = "#/pd";
      }
      console.log('App initialized!');
      this.appInitialized = true;

      const hash = window.location.hash;
      const isPrintRequest = hash.startsWith('#/rd') && hash.indexOf('PdfExportId') !== -1;
      if (!isPrintRequest) {
        //Pendo and Fullstory don't need to be waited for
        this.initializePendo(userEmail).then((pendoInitialized: boolean) => this.initializeFullStory(userEmail, userDescription, pendoInitialized)).catch(_e => {
          //Nothing
        });
      }
    }
  }

  /**
   * Handles an error if no user object could be retrieved (no user account / login issues).
   * @param error
   * @param window
   */
  private handleError(error: any, window: Window) {
    console.log(error);
    const noUO = error.status === 500
      && error.data.InnerException
      && error.data.InnerException.ExceptionMessage
      && error.data.InnerException.ExceptionMessage.indexOf("No UO") !== -1;

    console.log(`No UO: ${noUO}`);
    if (noUO) {
      //user is authenticated, but no UO in database
      window.location.hash = "#/unauthorized";
    } else {
      //some other error occured, probably: User is not authenticated!
      //Redirect to login (at least for Okta)
      const requestedUrl = window.location.href;
      sessionStorage.setItem('app:requestedUrl', requestedUrl);
      window.location.hash = "#/unauthorized?login=true";
    }
  }

  /**
   * Initializes Pendo
   * @param userEmail The user's mail address
   * @returns A Promise to wait for. The application start-up shouldn't be delayed until Pendo is initialzed
   */
  private initializePendo(userEmail: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (environment.PendoApiKey) {
        const TIMEOUT = 25;
        const MAX_WAIT_TIME = 10000;
        let retries = MAX_WAIT_TIME / TIMEOUT;

        const pendoInterval = setInterval(() => {
          retries--;
          if (retries === 0 || this.pendo) {
            clearInterval(pendoInterval);
          }

          if (this.pendo) {
            //for more options see: https://support.pendo.io/hc/en-us/articles/360046272771-Developer-s-guide-to-installing-Pendo#initialization-0-6

            const visitorId = userEmail;
            const accountId = environment.PendoAccountId || 'ACCOUNT-UNIQUE-ID';

            this.pendo.initialize({
              visitor: {
                id: visitorId // Required if user is logged in, default creates anonymous ID
              },
              account: {
                id: accountId // Required if using Pendo Feedback, default uses the value 'ACCOUNT-UNIQUE-ID'
              }
            });
            resolve(true);
          }
        }, TIMEOUT);
      }
      else {
        resolve(false);
      }
    });
  }

  /**
   * Initializes Fullstory
   * @param userEmail The user's mail address
   * @param userDescription The user's description
   */
  private initializeFullStory(userEmail: string, userDescription: string, pendoInitialized: boolean): void {
    if (environment.FullStoryOrgId) {
      FullStory.init({
        orgId: environment.FullStoryOrgId,
        host: environment.FullStoryHost,
        script: environment.FullStoryScript,
        devMode: !environment.production//set to false when testing FullStory in DEV
      }, ({ sessionUrl }) => {
        !environment.production && console.log(`Started FullStory session: ${sessionUrl}`);

        FullStory.identify(userEmail, {
          displayName: userDescription,
          email: userEmail
        });

        if (pendoInitialized) {
          const accountId = environment.PendoAccountId || 'ACCOUNT-UNIQUE-ID';
          this.pendo.identify({
            visitor: {
              id: userEmail,
              mostRecentFullStorySession: sessionUrl
            },
            account: {
              id: accountId // Required if using Pendo Feedback, default uses the value 'ACCOUNT-UNIQUE-ID'
            }
          });
        }
      });
    }
  }

  /**
   * Pings the token ping url to keep the token active
   * @param tokenPingUrl token ping url
   * @param pingInterval pig url in milliseconds
   */
  private pingTokenUrl(tokenPingUrl, pingInterval): void {
    setTimeout(() => {
      this.http.get(tokenPingUrl).subscribe({
        next: () => {
          //ping again
          this.pingTokenUrl(tokenPingUrl, pingInterval);
        }, error: () => {
          console.error("An error occurred while fetching the token - trying to reset the login state after a potential token expiry");
          this.window.location.href = "reset.html";
        }
      });
    }, pingInterval);
  }
}
